import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

function Bone({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <Skeleton
      className={cn('bg-black/[0.06] dark:bg-white/10', className)}
      {...props}
    />
  );
}

function ProductCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-black/5">
      <Bone className="aspect-square w-full rounded-none" />
      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <Bone className="h-4 w-3/4" />
        <Bone className="mt-3 h-4 w-1/2" />
        <Bone className="mt-1 h-3 w-1/3" />
        <Bone className="mt-4 h-9 w-full rounded-full" />
      </div>
    </div>
  );
}

export function StorefrontPageLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading store"
      className="min-h-screen bg-white animate-in fade-in duration-300"
    >
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[96%] items-center gap-3 sm:h-16 lg:max-w-[80%]">
          <div className="flex min-w-0 shrink-0 items-center gap-2.5">
            <Bone className="size-8 rounded-full sm:size-9" />
            <Bone className="h-4 w-28 sm:h-5 sm:w-36" />
          </div>
          <div className="mx-auto hidden max-w-md flex-1 sm:flex">
            <Bone className="h-10 w-full rounded-full" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Bone className="size-10 rounded-full sm:hidden" />
            <Bone className="h-10 w-10 rounded-full sm:w-[5.5rem]" />
          </div>
        </div>
      </header>

      <section className="border-b border-black/5">
        <div className="relative mx-auto max-w-[96%] lg:max-w-[80%]">
          <Bone className="h-40 w-full rounded-none rounded-b-[1.75rem] sm:h-52 md:h-64 md:rounded-b-[2.25rem]" />
          <div className="relative z-10 -mt-14 flex justify-center sm:-mt-16 md:-mt-[4.5rem]">
            <div className="rounded-full bg-white p-1.5 shadow-[0_12px_32px_-16px_rgba(0,0,0,0.35)] ring-1 ring-black/5">
              <Bone className="size-28 rounded-full sm:size-32 md:size-36" />
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[96%] px-4 pb-10 pt-5 text-center lg:max-w-[80%]">
          <Bone className="mx-auto h-3 w-20" />
          <Bone className="mx-auto mt-3 h-9 w-56 sm:h-11 sm:w-72" />
          <Bone className="mx-auto mt-3 h-4 w-full max-w-md" />
          <Bone className="mx-auto mt-2 h-4 w-2/3 max-w-sm" />
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
            <Bone className="h-11 w-32 rounded-full" />
            <Bone className="h-11 w-28 rounded-full" />
            <Bone className="h-11 w-11 rounded-full" />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[96%] px-4 py-10 sm:py-14 lg:max-w-[80%]">
        <div className="mb-6">
          <Bone className="h-3 w-16" />
          <Bone className="mt-2 h-8 w-40 sm:h-9 sm:w-48" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </main>
    </div>
  );
}

export function ProductDetailLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading product"
      className="min-h-screen bg-white animate-in fade-in duration-300"
    >
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[96%] items-center gap-3 sm:h-16 lg:max-w-[80%]">
          <Bone className="h-9 w-20 rounded-full" />
          <div className="ml-auto flex min-w-0 items-center gap-2.5">
            <Bone className="size-8 rounded-full" />
            <Bone className="h-4 w-28" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[96%] px-4 py-8 sm:py-12 lg:max-w-[80%]">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="space-y-4">
            <Bone className="aspect-square w-full rounded-[1.75rem]" />
            <div className="flex gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Bone key={i} className="size-20 shrink-0 rounded-2xl" />
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <Bone className="h-3 w-24" />
            <Bone className="h-10 w-4/5 sm:h-12" />
            <Bone className="h-8 w-32" />
            <div className="space-y-2">
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-2/3" />
            </div>
            <Bone className="h-10 w-36 rounded-full" />
            <Bone className="h-12 w-full rounded-full" />
            <div className="grid grid-cols-3 gap-3 border-t border-black/5 pt-6">
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-full" />
              <Bone className="h-4 w-full" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export function CheckoutLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading checkout"
      className="mx-auto max-w-5xl px-4 py-8 animate-in fade-in duration-300"
    >
      <div className="mb-8 flex items-center gap-3">
        <Bone className="h-9 w-20 rounded-full" />
        <Bone className="h-8 w-36" />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          <div className="space-y-4 rounded-2xl border border-black/8 bg-white p-6">
            <div className="flex items-center gap-3">
              <Bone className="size-6 rounded-full" />
              <div className="space-y-2">
                <Bone className="h-4 w-40" />
                <Bone className="h-3 w-56" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Bone className="h-10 w-full rounded-md" />
              <Bone className="h-10 w-full rounded-md" />
              <Bone className="h-10 w-full rounded-md" />
              <Bone className="h-10 w-full rounded-md" />
            </div>
            <Bone className="h-24 w-full rounded-md" />
          </div>
          <div className="space-y-4 rounded-2xl border border-black/8 bg-white p-6">
            <Bone className="h-4 w-32" />
            <Bone className="h-10 w-full rounded-md" />
            <Bone className="h-10 w-full rounded-md" />
          </div>
        </div>

        <div className="space-y-4 rounded-2xl border border-black/8 bg-white p-6">
          <Bone className="h-5 w-28" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <Bone className="size-14 shrink-0 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Bone className="h-4 w-3/4" />
                <Bone className="h-3 w-1/2" />
              </div>
            </div>
          ))}
          <Bone className="h-px w-full" />
          <Bone className="h-5 w-full" />
          <Bone className="h-12 w-full rounded-full" />
        </div>
      </div>
    </div>
  );
}
